import { AvatarNotificationBadge } from "@arcadeai/design-system/components/ui/atoms/avatar-notification-badge";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@arcadeai/design-system/components/ui/atoms/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from "@arcadeai/design-system/components/ui/atoms/sidebar";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import { UserNav } from "@arcadeai/design-system/components/ui/molecules/user-nav";
import { Link } from "@tanstack/react-router";
import { CreditCardIcon, LogOutIcon } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { Suspense } from "react";
import { toast } from "sonner";
import { useGetSubscription } from "@/app/billing/api/hooks";
import type { SubscriptionResponse } from "@/app/billing/types";
import { getPlanTierFromId } from "@/app/billing/upgrade/utils";
import { useGetCapabilities } from "@/app/capabilities/api/hooks";
import { useInvitationsBadgeCount } from "@/app/invitations/hooks/use-invitations-badge-count";
import { useSession } from "@/lib/auth-client";
import { logoutAndRedirect } from "@/lib/logout";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { InvitationsMenuItem } from "./invitations-menu-item";
import { UpgradeDropdownItem } from "./upgrade-card";

function NavUserSkeleton() {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <div className="flex w-full items-center gap-3 rounded-lg px-1.5 py-2">
          <Skeleton className="size-9 shrink-0 rounded-lg" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5 group-data-[collapsible=icon]:hidden">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

function NavUserContent({
  billingVisible,
  subscription,
}: {
  billingVisible: boolean;
  subscription?: SubscriptionResponse;
}) {
  const { data: session } = useSession();
  const { isMobile, state } = useSidebar();
  const posthog = usePostHog();
  const { count: invitationCount } = useInvitationsBadgeCount();

  const isCollapsed = state === "collapsed";
  const avatarSize = isCollapsed ? "sm" : "lg";
  const externalPlanId = subscription?.plan?.external_plan_id ?? undefined;
  const planId = billingVisible ? externalPlanId : "enterprise";
  const planTier = planId ? getPlanTierFromId(planId) : null;

  if (!session?.user) {
    return null;
  }

  const userName = session.user.name || session.user.email || "User";
  // When the user has no name, the email becomes the display name — don't repeat
  // it on the secondary line.
  const secondaryText =
    session.user.email === userName ? undefined : session.user.email;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <UserNav
          avatarSize={avatarSize}
          badge={
            invitationCount > 0 ? (
              <AvatarNotificationBadge
                ariaLabel={`${invitationCount} unread invitation${invitationCount === 1 ? "" : "s"}`}
                decorative={false}
                size={avatarSize}
              />
            ) : undefined
          }
          compact={isCollapsed}
          dropdownSide={isMobile ? "bottom" : "right"}
          email={secondaryText}
          name={userName}
        >
          {billingVisible && planTier === "free" && (
            <>
              <DropdownMenuSeparator />
              <UpgradeDropdownItem />
              <DropdownMenuSeparator />
            </>
          )}

          <InvitationsMenuItem count={invitationCount} />
          <DropdownMenuSeparator />

          {billingVisible && (
            <>
              <DropdownMenuItem
                className="cursor-pointer"
                render={
                  <Link
                    from="/orgs/$organizationId/projects/$projectId/billing"
                    to="."
                  />
                }
              >
                <CreditCardIcon className="size-4" />
                Billing
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => {
              posthog?.capture("Logout");
              posthog?.reset();
              logoutAndRedirect().catch(() => {
                toast.error("Logout failed");
              });
            }}
          >
            <LogOutIcon className="size-4" />
            Log out
          </DropdownMenuItem>
        </UserNav>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function NavUser() {
  const { data: capabilities, isPending: capabilitiesPending } =
    useGetCapabilities();
  const billingVisible = capabilities?.billing.enabled ?? false;
  const { organizationId } = useOrgProjectRouteContext();
  const { data: subscription, isPending } = useGetSubscription(organizationId, {
    enabled: billingVisible,
  });

  if (capabilitiesPending || (billingVisible && isPending)) {
    return <NavUserSkeleton />;
  }

  return (
    <Suspense fallback={<NavUserSkeleton />}>
      <NavUserContent
        billingVisible={billingVisible}
        subscription={subscription}
      />
    </Suspense>
  );
}
